import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import { Alert } from '../../components';
import { Button, Input } from '../../components/ui';
import { Window } from '../../contexts';

// Reuses the My Products panel's adder styles — same controls, master flavour.
import './store-catalog-panel.scss';

/**
 * Retail Circle Admin → Shared Catalog (store-catalog feature).
 *
 * Master-only adders for the SHARED catalogue every store sees: Add brand,
 * Add model, and the Create Product button that used to sit on Store
 * Settings. Rows created by a master land with store_id_m NULL + approved
 * (SharedCatalog creating-hook) — no queue involved. Store admins add their
 * own private entries under Owner Admin → My Products instead.
 */
export function SharedCatalogAdminPanel() {
  const history = useHistory();

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const [newBrand, setNewBrand] = useState('');
  const [newModelBrandId, setNewModelBrandId] = useState('');
  const [newModel, setNewModel] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);

  const categoryId = (Window.CATEGORIES || []).find((c) => c.url_name === 'watches')?.id;

  const loadBrandOptions = async () => {
    if (!categoryId) return;
    try {
      const res = await axios.get(`/api/categories/${categoryId}/brands?all=true`);
      setBrandOptions(res.data || []);
    } catch (e) {
      setBrandOptions([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBrandOptions(); }, []);

  const run = async (fn, successNote, fallbackMessage) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(successNote);
      await loadBrandOptions();
    } catch (e) {
      const payload = e?.response?.data;
      setError(
        payload?.message
          || Object.values(payload?.errors || {}).flat().join(' ')
          || fallbackMessage
      );
    } finally {
      setBusy(false);
    }
  };

  const addBrand = () => {
    const name = newBrand.trim();
    if (!name || !categoryId) return;
    run(async () => {
      await axios.post('/api/brands', { category_id: categoryId, name });
      setNewBrand('');
    }, `Brand "${name}" added to the shared catalogue.`, 'Could not add the brand.');
  };

  const addModel = () => {
    const name = newModel.trim();
    if (!name || !newModelBrandId) return;
    run(async () => {
      await axios.post('/api/models', { brand_id: Number(newModelBrandId), name });
      setNewModel('');
    }, `Model "${name}" added to the shared catalogue.`, 'Could not add the model.');
  };

  return (
    <div className="scp">
      <p className="scp__intro">
        The shared Retail Circle catalogue every store sees. Entries added here
        go live immediately — no approval queue.
      </p>

      {error && <Alert type="error" message={error} />}
      {notice && <Alert type="success" message={notice} />}

      <div className="scp__adders">
        <div className="scp__adder">
          <span className="scp__adder-label">Add a brand</span>
          <div className="scp__adder-row">
            <Input
              size="md"
              placeholder="Brand name"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addBrand()}
            />
            <Button size="md" variant="secondary" disabled={busy || !newBrand.trim()} onClick={addBrand}>
              Add brand
            </Button>
          </div>
        </div>

        <div className="scp__adder">
          <span className="scp__adder-label">Add a model</span>
          <div className="scp__adder-row">
            <select
              value={newModelBrandId}
              onChange={(e) => setNewModelBrandId(e.target.value)}
              aria-label="Brand for the new model"
            >
              <option value="">Select brand…</option>
              {brandOptions.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <Input
              size="md"
              placeholder="Model name"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addModel()}
            />
            <Button size="md" variant="secondary" disabled={busy || !newModel.trim() || !newModelBrandId} onClick={addModel}>
              Add model
            </Button>
          </div>
        </div>

        <div className="scp__adder">
          <span className="scp__adder-label">Create a product</span>
          <div className="scp__adder-row">
            <Button size="md" variant="primary" onClick={() => history.push('/add-new-product')}>
              Open the product form
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharedCatalogAdminPanel;
